import React from "react"

import { FloatingPortal } from "@floating-ui/react"
import type { Panel } from "@logic/lib/design"
import { useRecoilValue, useSetRecoilState } from "recoil"
import styled, { css } from "styled-components"

import { SummaryTable, returnRow } from "~/components/AreaSummaryTable"
import Button from "~/components/Button"
import { Modal } from "~/components/ui"
import { safeEstimatesRemoteState } from "~/containers/Proposal/state/designs/design-state"
import { selectedInstallAreaIdState } from "~/containers/Proposal/state/designs/installAreas"
import { productivePanelsState } from "~/containers/Proposal/state/designs/panels"

import { ReactComponent as CloseIcon } from "@svg/close.svg"

import { allAreasState, areaIndex } from "../state/elements"
import { areasOfPanelsIndexArrayState } from "../state/elements/relations"

const productiveModsForArea = (
  areaId: string,
  areaIndex: [string, Set<string>][],
  productiveMods: (Panel & { id: string })[],
): (Panel & { id: string })[] => {
  const mods = areaIndex.find(([id]) => areaId === id)?.[1]
  if (!mods) return []

  const productiveModsForArea = productiveMods.filter((mod) =>
    [...mods].includes(mod.id),
  )

  return productiveModsForArea
}

export const Summary: React.FC = () => {
  const [showArraySummary, setShowArraySummary] = React.useState(false)

  return (
    <>
      <Wrapper>
        <Button
          variant="solid"
          style={{
            background: `var(--blue)`,
          }}
          onClick={() => setShowArraySummary(true)}
        >
          View Summary
        </Button>
      </Wrapper>
      {/* Animating this causes it to not disappear in dev */}
      {showArraySummary ? (
        <FloatingPortal id="array-modal" key="array-modal">
          <SummaryModal setShow={setShowArraySummary} />
        </FloatingPortal>
      ) : null}
    </>
  )
}

const ModalStyle = css`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  justify-content: center;
  align-items: center;
  position: fixed;
  top: 0;
  left: 0;
  z-index: 9000;
  background: var(--blue-dark-transparent);
  overflow: hidden;
  & > div {
    margin: auto;
    width: calc(100% - 40px);
    max-width: 700px;
    background: #ffffff;
    filter: drop-shadow(0px 10px 20px rgba(0, 0, 0, 0.16));
    border-radius: 8px;
    overflow: hidden;
    overflow-x: auto;
    padding: 20px;
  }
`

const Section = styled.section`
  width: 100%;
  header {
    display: flex;
    width: 100%;
    justify-content: space-between;
    align-items: center;
    padding-bottom: var(--space-s);
    h1 {
      color: var(--blue);
      font-weight: 500;
      font-size: var(--step-1);
    }
    > button {
      cursor: pointer;
      > svg {
        font-size: var(--step--1);
      }
    }
  }
  > h1 {
    color: var(--blue);
    font-weight: 500;
    font-size: var(--step-1);
    padding-bottom: var(--space-s);
  }
`

const SummaryModalInternal: React.FC<{
  setShow: (show: boolean) => void
}> = ({ setShow }) => {
  const areas = useRecoilValue(areaIndex)
  const allAreas = useRecoilValue(allAreasState)
  const areasOfPanelsIndex = useRecoilValue(areasOfPanelsIndexArrayState)
  const estimates = useRecoilValue(safeEstimatesRemoteState)
  const allActiveMods = useRecoilValue(productivePanelsState)
  const setSelectedInstallAreaId = useSetRecoilState(selectedInstallAreaIdState)
  const rows = areas.map((areaId, index) =>
    returnRow({
      id: areaId,
      mods: Array.from(
        areasOfPanelsIndex.find(([id]) => id === areaId)?.[1] || new Set(),
      ),
      index,
      area: allAreas[areaId],
      estimates,
      activeMods: productiveModsForArea(
        areaId,
        areasOfPanelsIndex,
        allActiveMods,
      ),
    }),
  )

  return (
    <Modal onClose={() => setShow(false)} css={ModalStyle}>
      <Section>
        <header>
          <h1>All Areas</h1>
          <button onClick={() => setShow(false)}>
            <CloseIcon />
          </button>
        </header>
        <SummaryTable
          rows={rows}
          onClickLegacy={(id) => (setSelectedInstallAreaId(id), setShow(false))}
        />
      </Section>
    </Modal>
  )
}

const SummaryModal = React.memo(SummaryModalInternal)

const Wrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px 0;
  background-color: var(--white);
  > button {
    width: calc(100% - 20px);
    min-width: fit-content;
  }
`
